import axios, { AxiosPromise } from 'axios'

import {
  CompanyLookupResponse,
  CompanySuggestionResponse,
  DeclinedJoinRequestResponse,
  JoinRequestResponse,
  MembershipResponse,
} from '@/lib/definitions/api.types'
import { apiUrl, getAxiosRequestConfigWithHeaders } from '@/lib/misc/Utils'

const BASE_URL = '/api/v1/company-membership'

const untenantedConfig = () =>
  getAxiosRequestConfigWithHeaders(undefined, undefined, false)

export class MembershipService {
  lookupCompany(name: string): AxiosPromise<CompanyLookupResponse> {
    return axios.get(
      apiUrl(`${BASE_URL}/lookup`),
      getAxiosRequestConfigWithHeaders(undefined, { params: { name } })
    )
  }

  getCompanySuggestions(): AxiosPromise<CompanySuggestionResponse[]> {
    return axios.get(
      apiUrl(`${BASE_URL}/suggestions`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  createJoinRequest(customerId: number): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/join-requests`),
      { customer_id: customerId },
      getAxiosRequestConfigWithHeaders()
    )
  }

  getMyJoinRequests(): AxiosPromise<JoinRequestResponse[]> {
    return axios.get(
      apiUrl(`${BASE_URL}/join-requests/mine`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getMyJoinRequest(requestId: number): AxiosPromise<JoinRequestResponse> {
    return axios.get(
      apiUrl(`${BASE_URL}/join-requests/mine/${requestId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  withdrawJoinRequest(requestId: number): AxiosPromise {
    return axios.delete(
      apiUrl(`${BASE_URL}/join-requests/${requestId}`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  getJoinRequests(): AxiosPromise<JoinRequestResponse[]> {
    return axios.get(
      apiUrl(`${BASE_URL}/join-requests`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  acceptJoinRequest(requestId: number): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/join-requests/${requestId}/accept`),
      {},
      getAxiosRequestConfigWithHeaders()
    )
  }

  rejectJoinRequest(requestId: number): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/join-requests/${requestId}/reject`),
      {},
      getAxiosRequestConfigWithHeaders()
    )
  }

  getDeclinedJoinRequests(): AxiosPromise<DeclinedJoinRequestResponse[]> {
    return axios.get(
      apiUrl(`${BASE_URL}/join-requests/declined`),
      getAxiosRequestConfigWithHeaders()
    )
  }

  approveDeclinedJoinRequest(
    requestId: number
  ): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/join-requests/${requestId}/approve`),
      {},
      getAxiosRequestConfigWithHeaders()
    )
  }

  getMyInvitations(): AxiosPromise<JoinRequestResponse[]> {
    return axios.get(apiUrl(`${BASE_URL}/invitations`), untenantedConfig())
  }

  acceptInvitation(requestId: number): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/invitations/${requestId}/accept`),
      {},
      untenantedConfig()
    )
  }

  declineInvitation(requestId: number): AxiosPromise<JoinRequestResponse> {
    return axios.post(
      apiUrl(`${BASE_URL}/invitations/${requestId}/decline`),
      {},
      untenantedConfig()
    )
  }

  getMemberships(): AxiosPromise<MembershipResponse[]> {
    return axios.get(apiUrl(`${BASE_URL}/memberships`), untenantedConfig())
  }

  leaveCompany(): AxiosPromise {
    return axios.post(apiUrl(`${BASE_URL}/leave`), {}, untenantedConfig())
  }

  leaveMembership(customerId: number): AxiosPromise {
    return axios.delete(
      apiUrl(`${BASE_URL}/memberships/${customerId}`),
      untenantedConfig()
    )
  }
}
